import React, { createContext, useState, useContext, ReactNode, useEffect, useMemo, useCallback, useRef } from 'react';
import { toast } from '@/components/ui/use-toast';
import { useSession } from '@/context/SessionContext';
import { AppNotification, getNotificationsByUserId, markNotificationAsRead, markNotificationAsUnread, clearUserNotifications } from '@/lib/supabase';
import { supabase } from '@/lib/supabase';
import { RealtimeChannel } from '@supabase/supabase-js';
import { useNavigate } from 'react-router-dom';
import { getToastActionForNotification, inferNotificationTypeFromMessage } from '@/lib/toastActions';

type NotificationContextType = {
  notifications: AppNotification[];
  unreadCount: number;
  markAsRead: (id: string) => Promise<void>;
  markAsUnread: (id: string) => Promise<void>;
  clearNotifications: () => Promise<void>;
  markAllAsRead: () => Promise<void>;
  loadingNotifications: boolean;
  fetchNotifications: () => Promise<void>;
  sidebarOpen: boolean;
  toggleSidebar: () => void;
  openSidebar: () => void;
  closeSidebar: () => void;
  closeWithDelay: (delayMs?: number) => void;
  cancelCloseDelay: () => void;
};

export const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const NotificationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { session, loading: sessionLoading } = useSession();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [loadingNotifications, setLoadingNotifications] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const currentUserId = session?.user?.id;
  const closeTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Fetch notifications once on session load
  const fetchNotifications = useCallback(async () => {
    if (!currentUserId) return;
    setLoadingNotifications(true);
    try {
      const fetched = await getNotificationsByUserId(currentUserId);
      setNotifications(fetched);
    } finally {
      setLoadingNotifications(false);
    }
  }, [currentUserId]);

  useEffect(() => {
    if (!sessionLoading && currentUserId) {
      fetchNotifications();
    }
    if (!currentUserId) {
      setNotifications([]);
      setLoadingNotifications(false);
    }
  }, [sessionLoading, currentUserId, fetchNotifications]);

  // Fallback refresh strategy in case realtime events are delayed/missed.
  useEffect(() => {
    if (!currentUserId) return;

    // Increased from 15s to 60s to reduce egress
    // Fetching all notifications every 15s can consume 50-200MB/month per user
    const intervalId = setInterval(() => {
      fetchNotifications();
    }, 60000);

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        fetchNotifications();
      }
    };

    window.addEventListener('focus', fetchNotifications);
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      clearInterval(intervalId);
      window.removeEventListener('focus', fetchNotifications);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [currentUserId, fetchNotifications]);

  // Realtime subscription
  useEffect(() => {
    if (sessionLoading || !currentUserId) return;
    const channel: RealtimeChannel = supabase
      .channel(`user_notifications_${currentUserId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${currentUserId}`
        },
        (payload) => {
          const n = payload.new as AppNotification;
          setNotifications(prev => {
            if (prev.some(x => x.id === n.id)) return prev;
            return [n, ...prev];
          });
          
          // Infer notification type from message
          const notificationType = inferNotificationTypeFromMessage(n.message);
          
          // Get action for this notification type
          const openOrderSidebar = (orderId: string) => {
            window.dispatchEvent(new CustomEvent('open-order-sidebar', { detail: { orderId } }));
          };
          const onAction = getToastActionForNotification(
            notificationType,
            { ...(n as any).data, link: n.link },
            navigate,
            openOrderSidebar
          );
          
          toast({
            title: 'Nueva notificación',
            description: n.message,
            onAction,
          });
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          fetchNotifications();
        }
      });
    window.__dyadNotifChannel = channel;
    return () => {
      supabase.removeChannel(channel);
    };
  }, [currentUserId, sessionLoading, fetchNotifications, navigate]);
  // Mark all as read
  const markAllAsRead = useCallback(async () => {
    if (!currentUserId) return;
    try {
      const unread = notifications.filter(n => !n.is_read).map(n => n.id);
      if (unread.length === 0) return;
      await Promise.all(unread.map(id => markNotificationAsRead(id, currentUserId)));
      await fetchNotifications();
    } catch {}
  }, [currentUserId, notifications, fetchNotifications]);

  const unreadCount = useMemo(() => notifications.filter(n => !n.is_read).length, [notifications]);

  const markAsRead = useCallback(async (id: string) => {
    if (!currentUserId) return;
    try {
      await markNotificationAsRead(id, currentUserId);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
    } catch {}
  }, [currentUserId]);

  const markAsUnread = useCallback(async (id: string) => {
    if (!currentUserId) return;
    try {
      await markNotificationAsUnread(id, currentUserId);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: false } : n));
    } catch {}
  }, [currentUserId]);

  const clearNotifications = useCallback(async () => {
    if (!currentUserId) return;
    try {
      await clearUserNotifications(currentUserId);
      setNotifications([]);
    } catch {}
  }, [currentUserId]);

  const toggleSidebar = useCallback(() => {
    setSidebarOpen(prev => !prev);
  }, []);

  const openSidebar = useCallback(() => {
    // Forzar recálculo del header inmediatamente antes de abrir
    window.dispatchEvent(new Event('recalculate-header'));
    void fetchNotifications();
    // Pequeño delay para asegurar que el recálculo se complete
    requestAnimationFrame(() => {
      setSidebarOpen(true);
      window.dispatchEvent(new CustomEvent('sidebar-opened', { detail: { type: 'notifications' } }));
    });
  }, [fetchNotifications]);

  const closeSidebar = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    setSidebarOpen(false);
  }, []);

  const cancelCloseDelay = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const closeWithDelay = useCallback((delayMs: number = 1000) => {
    cancelCloseDelay();
    closeTimerRef.current = setTimeout(() => {
      setSidebarOpen(false);
      closeTimerRef.current = null;
    }, delayMs);
  }, [cancelCloseDelay]);

  useEffect(() => {
    return () => {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const handleSidebarOpened = (event: CustomEvent) => {
      if (event.detail.type !== 'notifications') {
        if (closeTimerRef.current) {
          clearTimeout(closeTimerRef.current);
          closeTimerRef.current = null;
        }
        setSidebarOpen(false);
      }
    };
    window.addEventListener('sidebar-opened', handleSidebarOpened as EventListener);
    return () => {
      window.removeEventListener('sidebar-opened', handleSidebarOpened as EventListener);
    };
  }, []);

  return (
    <NotificationContext.Provider value={{
      notifications, 
      unreadCount, 
      markAsRead, 
      markAsUnread, 
      clearNotifications, 
      markAllAsRead, 
      loadingNotifications, 
      fetchNotifications, 
      sidebarOpen, 
      toggleSidebar, 
      openSidebar, 
      closeSidebar,
      closeWithDelay,
      cancelCloseDelay
    }}>
      {children}
    </NotificationContext.Provider>
  );
};


export function useNotifications() {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
}
